import { useMemo } from 'react';
import type { Filters, GuestRating } from '@shared/types/Accommodations.types';
import { AccommodationCategory } from '@graphql/generated-base';
import { useAccommodationsConsoleSearchParamsHelper } from '../useAccommodationsConsoleSearchParamsHelper';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useLocation } from '@react-router';
import { useUpdateAccommodationsConsoleSearchParams } from './useUpdateAccommodationsConsoleSearchParams';
import type { SortOption } from '@shared/constants/accommodations';
import { FILTER_QUERY_PARAM_KEYS, DEFAULT_FILTERS, SORT_QUERY_PARAM_KEY } from './constants';
import { PRICE_RANGE } from '../../constants';

const VALID_ACCOMMODATION_CATEGORIES = new Set<string>(Object.values(AccommodationCategory));

export type UseFilterQueryParamsReturn = ReturnType<typeof useFilterQueryParams>;

const stringValueToInt = (value: string) => parseInt(value, 10);

// Encode/decode individual values so commas inside names survive the comma-delimited URL param round-trip.
// Values are intentionally pre-encoded before URLSearchParams adds its own encoding layer;
// on read-back, qs.parse peels off one layer and decodeFilterValues peels off the second.
export const encodeFilterValues = (values: ReadonlyArray<string>): string => values.map(encodeURIComponent).join(',');
export const safeDecodeURIComponent = (str: string): string => {
  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
};
export const decodeFilterValues = (str: string): string[] =>
  str
    .split(',')
    .map(v => safeDecodeURIComponent(v.trim()))
    .filter(Boolean);

export const hasActiveFilters = (filters: Filters): boolean =>
  filters.facilities.length > 0 ||
  filters.starRating.length > 0 ||
  filters.userRating.length > 0 ||
  filters.propertyCategories.length > 0 ||
  filters.brands.length > 0 ||
  filters.cug ||
  filters.pickedByCouple ||
  !filters.showAvailableOnly ||
  filters.priceRange[0] !== PRICE_RANGE.MIN ||
  filters.priceRange[1] !== PRICE_RANGE.MAX ||
  filters.freeCancellation ||
  !!filters.cashFundFullyCoveredOnly;

export const useFilterQueryParams = () => {
  const location = useLocation();
  const queryParamHelper = useAccommodationsConsoleSearchParamsHelper();
  const { applyAccommodationConsoleSearchParamsToUrl } = useUpdateAccommodationsConsoleSearchParams();
  const currentFilters = useMemo<Filters>(() => {
    const newFilters: UnsealedReadonly<Filters> = { ...DEFAULT_FILTERS };

    const facilitiesStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.FACILITIES);
    if (facilitiesStr) newFilters.facilities = decodeFilterValues(facilitiesStr).sort();

    const cugStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.CUG);
    if (cugStr) newFilters.cug = cugStr === 'true';

    const starRatingStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.STAR_RATING);
    if (starRatingStr)
      newFilters.starRating = starRatingStr
        .split(',')
        .map(stringValueToInt)
        .sort((a, b) => a - b);

    const userRatingStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.USER_RATING);
    if (userRatingStr)
      newFilters.userRating = userRatingStr
        .split(',')
        .map(stringValueToInt)
        .sort((a, b) => a - b) as GuestRating[];

    const priceMin = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.PRICE_MIN);
    const priceMax = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.PRICE_MAX);
    if (priceMin && priceMax) {
      newFilters.priceRange = [stringValueToInt(priceMin), stringValueToInt(priceMax)];
    }

    const showAvailableOnlyStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.SHOW_AVAILABLE_ONLY);
    if (showAvailableOnlyStr) newFilters.showAvailableOnly = showAvailableOnlyStr === 'true';

    const pickedByCoupleStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.PICKED_BY_COUPLE);
    if (pickedByCoupleStr) newFilters.pickedByCouple = pickedByCoupleStr === 'true';

    // Enum values — no encoding needed (AccommodationCategory values never contain commas)
    const propertyCategoriesStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.PROPERTY_CATEGORIES);
    if (propertyCategoriesStr) {
      newFilters.propertyCategories = propertyCategoriesStr.split(',').filter((v): v is AccommodationCategory => VALID_ACCOMMODATION_CATEGORIES.has(v));
    }

    const brandsStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.BRANDS);
    if (brandsStr) newFilters.brands = decodeFilterValues(brandsStr).sort();

    const freeCancellationStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.FREE_CANCELLATION);
    if (freeCancellationStr) newFilters.freeCancellation = freeCancellationStr === 'true';

    const cashFundCoveredStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.CASH_FUND_COVERED);
    if (cashFundCoveredStr) newFilters.cashFundFullyCoveredOnly = cashFundCoveredStr === 'true';

    return newFilters;
  }, [queryParamHelper]);

  const updateFilters = useEventCallback((newFilters: Filters, actionType: 'push' | 'replace' = 'replace') => {
    const searchParams: Record<string, string | null> = {};
    // Update facilities
    searchParams[FILTER_QUERY_PARAM_KEYS.FACILITIES] = newFilters.facilities?.length ? encodeFilterValues(newFilters.facilities) : null;

    // Update CUG
    searchParams[FILTER_QUERY_PARAM_KEYS.CUG] = newFilters.cug ? 'true' : null;

    // Update star rating
    searchParams[FILTER_QUERY_PARAM_KEYS.STAR_RATING] = newFilters.starRating?.length ? newFilters.starRating.join(',') : null;

    // Update user rating
    searchParams[FILTER_QUERY_PARAM_KEYS.USER_RATING] = newFilters.userRating?.length ? newFilters.userRating.join(',') : null;

    // Update price range
    if (newFilters.priceRange) {
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MIN] = newFilters.priceRange[0].toString();
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MAX] = newFilters.priceRange[1].toString();
    } else {
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MIN] = null;
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MAX] = null;
    }

    // Update show available only
    searchParams[FILTER_QUERY_PARAM_KEYS.SHOW_AVAILABLE_ONLY] = newFilters.showAvailableOnly ? null : 'false';

    // Update picked by couple
    searchParams[FILTER_QUERY_PARAM_KEYS.PICKED_BY_COUPLE] = newFilters.pickedByCouple ? 'true' : null;

    // Update property categories
    searchParams[FILTER_QUERY_PARAM_KEYS.PROPERTY_CATEGORIES] = newFilters.propertyCategories?.length ? newFilters.propertyCategories.join(',') : null;

    // Update brands
    searchParams[FILTER_QUERY_PARAM_KEYS.BRANDS] = newFilters.brands?.length ? encodeFilterValues(newFilters.brands) : null;

    searchParams[FILTER_QUERY_PARAM_KEYS.FREE_CANCELLATION] = newFilters.freeCancellation ? 'true' : null;

    searchParams[FILTER_QUERY_PARAM_KEYS.CASH_FUND_COVERED] = newFilters.cashFundFullyCoveredOnly ? 'true' : null;

    applyAccommodationConsoleSearchParamsToUrl({ searchParams, actionType, locationHash: location.hash });
  });

  const resetFilters = useEventCallback((sortBy?: SortOption) => {
    const searchParams: Record<string, string | null> = {
      [FILTER_QUERY_PARAM_KEYS.FACILITIES]: null,
      [FILTER_QUERY_PARAM_KEYS.CUG]: null,
      [FILTER_QUERY_PARAM_KEYS.STAR_RATING]: null,
      [FILTER_QUERY_PARAM_KEYS.USER_RATING]: null,
      [FILTER_QUERY_PARAM_KEYS.PRICE_MIN]: DEFAULT_FILTERS.priceRange[0].toString(),
      [FILTER_QUERY_PARAM_KEYS.PRICE_MAX]: DEFAULT_FILTERS.priceRange[1].toString(),
      [FILTER_QUERY_PARAM_KEYS.SHOW_AVAILABLE_ONLY]: null,
      [FILTER_QUERY_PARAM_KEYS.PICKED_BY_COUPLE]: null,
      [FILTER_QUERY_PARAM_KEYS.PROPERTY_CATEGORIES]: null,
      [FILTER_QUERY_PARAM_KEYS.BRANDS]: null,
      [FILTER_QUERY_PARAM_KEYS.FREE_CANCELLATION]: null,
      [FILTER_QUERY_PARAM_KEYS.CASH_FUND_COVERED]: null,
      [SORT_QUERY_PARAM_KEY]: sortBy ?? null,
    };

    applyAccommodationConsoleSearchParamsToUrl({ searchParams, actionType: 'replace', locationHash: location.hash });
  });

  return {
    currentFilters,
    hasActiveFilters: hasActiveFilters(currentFilters),
    updateFilters,
    resetFilters,
  };
};
